<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { onBeforeUnmount, watch } from 'vue'

const props = defineProps<{
  modelValue: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const editor = useEditor({
  content: props.modelValue,
  extensions: [StarterKit],
  editorProps: {
    attributes: {
      class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
    },
  },
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
})

// Watch for external modelValue changes
watch(
  () => props.modelValue,
  (newContent) => {
    const editorInstance = editor.value
    if (!editorInstance) return

    // Only update if content is different
    if (editorInstance.getHTML() === newContent) return

    editorInstance.commands.setContent(newContent, false)
  }
)

// Clean up on unmount
onBeforeUnmount(() => {
  editor.value?.destroy()
})

const toggleBold = () => editor.value?.chain().focus().toggleBold().run()
const toggleItalic = () => editor.value?.chain().focus().toggleItalic().run()
const toggleStrike = () => editor.value?.chain().focus().toggleStrike().run()
const toggleCode = () => editor.value?.chain().focus().toggleCode().run()
const toggleBulletList = () => editor.value?.chain().focus().toggleBulletList().run()
const toggleOrderedList = () => editor.value?.chain().focus().toggleOrderedList().run()
const toggleBlockquote = () => editor.value?.chain().focus().toggleBlockquote().run()
const toggleHeading = (level: 1 | 2 | 3) =>
  editor.value?.chain().focus().toggleHeading({ level }).run()

const isActive = (type: string, attrs = {}) => editor.value?.isActive(type, attrs)
</script>

<template>
  <div class="editor border rounded-lg overflow-hidden">
    <div class="border-b bg-muted/40 p-2 flex flex-wrap gap-1">
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('bold') })"
        @click="toggleBold"
      >
        <Icon name="ph:text-b-bold" class="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('italic') })"
        @click="toggleItalic"
      >
        <Icon name="ph:text-italic" class="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('strike') })"
        @click="toggleStrike"
      >
        <Icon name="ph:text-strikethrough" class="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('code') })"
        @click="toggleCode"
      >
        <Icon name="ph:code" class="h-4 w-4" />
      </Button>
      <div class="w-px h-4 bg-border my-auto mx-1" />
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('heading', { level: 1 }) })"
        @click="toggleHeading(1)"
      >
        <Icon name="ph:text-h-one" class="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('heading', { level: 2 }) })"
        @click="toggleHeading(2)"
      >
        <Icon name="ph:text-h-two" class="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('heading', { level: 3 }) })"
        @click="toggleHeading(3)"
      >
        <Icon name="ph:text-h-three" class="h-4 w-4" />
      </Button>
      <div class="w-px h-4 bg-border my-auto mx-1" />
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('bulletList') })"
        @click="toggleBulletList"
      >
        <Icon name="ph:list-bullets" class="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('orderedList') })"
        @click="toggleOrderedList"
      >
        <Icon name="ph:list-numbers" class="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        :class="cn('px-2', { 'bg-muted': isActive('blockquote') })"
        @click="toggleBlockquote"
      >
        <Icon name="ph:quotes" class="h-4 w-4" />
      </Button>
    </div>
    <client-only>
      <EditorContent v-if="editor" :editor="editor" class="p-4" />
    </client-only>
  </div>
</template>

<style>
/* eslint-disable */
/* stylelint-disable */
/* CSS Validation Disable */
.ProseMirror {
  @apply min-h-[200px] outline-none;
}

.ProseMirror p.is-editor-empty:first-child::before {
  @apply text-muted-foreground;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

/* Basic editor styles */
.ProseMirror > * + * {
  margin-top: 0.75em;
}

.ProseMirror ul,
.ProseMirror ol {
  padding: 0 1rem;
}

.ProseMirror h1 {
  @apply text-4xl font-bold;
}

.ProseMirror h2 {
  @apply text-3xl font-bold;
}

.ProseMirror h3 {
  @apply text-2xl font-bold;
}

.ProseMirror code {
  @apply bg-muted rounded px-1.5 py-0.5 font-mono text-sm;
}

.ProseMirror pre {
  @apply bg-muted p-4 rounded-lg font-mono;
}

.ProseMirror blockquote {
  @apply pl-4 border-l-4 border-border italic;
}
</style>
